import { useState } from "react";
import type { OptionCategory, SelectedItems } from "@/types/recipe";

const INITIAL_SELECTION: SelectedItems = {
  intent: ["quick meal"],
  portions: 2,
  ingredients: {
    protein: ["chicken breasts"],
    vegetables: ["onion", "garlic"],
    sides: ["white rice"],
  },
  spices: ["paprika"],
  appliance: ["frying pans"],
  personal_preferences: "",
};

const toggle = (list: string[], value: string) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

export const useRecipeSelection = () => {
  const [selected, setSelected] = useState<SelectedItems>(INITIAL_SELECTION);

  const toggleSelection = (category: "intent" | "spices" | "appliance", value: string) => {
    setSelected((prev) => ({ ...prev, [category]: toggle(prev[category], value) }));
  };

  const toggleIngredient = (
    subcategory: keyof SelectedItems["ingredients"],
    value: string
  ) => {
    setSelected((prev) => ({
      ...prev,
      ingredients: {
        ...prev.ingredients,
        [subcategory]: toggle(prev.ingredients[subcategory], value),
      },
    }));
  };

  const setPortions = (updater: (current: number) => number) => {
    setSelected((prev) => ({ ...prev, portions: updater(prev.portions) }));
  };

  const setPreferences = (value: string) => {
    setSelected((prev) => ({ ...prev, personal_preferences: value }));
  };

  const removeFromSelected = (category: OptionCategory, option: string) => {
    setSelected((prev) => {
      switch (category) {
        case "intent":
          return { ...prev, intent: prev.intent.filter((item) => item !== option) };
        case "spices":
          return { ...prev, spices: prev.spices.filter((item) => item !== option) };
        case "appliance":
          return { ...prev, appliance: prev.appliance.filter((item) => item !== option) };
        case "protein":
        case "vegetables":
        case "sides":
          return {
            ...prev,
            ingredients: {
              ...prev.ingredients,
              [category]: prev.ingredients[category].filter((item) => item !== option),
            },
          };
        default:
          return prev;
      }
    });
  };

  return {
    selected,
    toggleSelection,
    toggleIngredient,
    setPortions,
    setPreferences,
    removeFromSelected,
  };
};
